import React from 'react'
import { observer } from 'mobx-react-lite'
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit'
import ContestStore, { MODES, NON_WINNER_AMOUNT } from '../Contest.store'

const ContestController = observer(({ onEndContest }) => {
  const { mode, isPreparing, isPlaying, isCompleted, visibleContestants, winners, contestants } = ContestStore

  const handleStartPlaying = () => {
    if (visibleContestants.length === 0) {
      alert('Please add at least one visible contestant before starting.')
      return
    }
    ContestStore.startPlaying()
  }

  const handleEndContest = async () => {
    if (ContestStore.isEnding) return

    if (winners.length === 0) {
      if (!window.confirm('No spins have been run. Cancel contest and go back to preparing?')) {
        return
      }
      ContestStore.resetContest()
      return
    }
    if (!window.confirm('Are you sure you want to end this contest? This will finalize all winnings.')) {
      return
    }
    ContestStore.setEnding(true)
    try {
      ContestStore.endContest()
      if (onEndContest) {
        await onEndContest()
      }
    } finally {
      ContestStore.setEnding(false)
    }
  }

  const handleReset = () => {
    if (!window.confirm('Reset the contest? This will clear all contestants and results.')) {
      return
    }
    ContestStore.resetContest()
  }

  const getModeLabel = () => {
    switch (mode) {
      case MODES.PREPARING:
        return 'Preparing'
      case MODES.PLAYING:
        return 'Playing'
      case MODES.COMPLETED:
        return 'Completed'
      default:
        return mode
    }
  }

  const getModeColor = () => {
    switch (mode) {
      case MODES.PREPARING:
        return '#ffc107'
      case MODES.PLAYING:
        return '#28a745'
      case MODES.COMPLETED:
        return '#6c757d'
      default:
        return '#333'
    }
  }

  return (
    <div className='contest-controller-component mb-4'>
      <div className='d-flex align-items-center justify-content-between flex-wrap'>
        <div>
          <span className='mr-2'>Mode:</span>
          <span
            className='badge badge-pill px-3 py-2'
            style={{
              backgroundColor: getModeColor(),
              color: '#fff',
              fontSize: '1rem',
            }}
          >
            {getModeLabel()}
          </span>
        </div>

        <div className='d-flex align-items-center'>
          {isPlaying && (
            <MDBBtn
              type='button'
              color={ContestStore.hideNamesList ? 'primary' : 'light'}
              onClick={() => ContestStore.toggleHideNamesList()}
              size='sm'
            >
              <MDBIcon icon={ContestStore.hideNamesList ? 'check-square' : 'square'} className='mr-2' />
              Hide names list
            </MDBBtn>
          )}

          {isPreparing && (
            <MDBBtn type='button' color='success' onClick={handleStartPlaying}>
              <MDBIcon icon='play' className='mr-1' /> Start Contest
            </MDBBtn>
          )}

          {isPlaying && (
            <MDBBtn color='danger' onClick={handleEndContest} disabled={ContestStore.isEnding}>
              <MDBIcon icon='stop' className='mr-1' /> {ContestStore.isEnding ? 'Ending…' : 'End Contest'}
            </MDBBtn>
          )}

          {false && isCompleted && (
            <MDBBtn type='button' color='info' onClick={handleReset}>
              <MDBIcon icon='redo' className='mr-1' /> New Contest
            </MDBBtn>
          )}
        </div>
      </div>

      {isCompleted && winners.length > 0 && (
        <div className='mt-3'>
          <h6>Contest Results:</h6>
          <ul className='list-unstyled'>
            {winners.map((w, idx) => (
              <li key={idx} className='mb-1'>
                <MDBIcon icon='trophy' className='text-warning mr-2' />
                <strong>{w.name}</strong> won <span className='text-success font-weight-bold'>${w.amount}</span> (Spin #
                {w.spinIndex + 1})
              </li>
            ))}
          </ul>
          {(() => {
            const winnerTotal = winners.reduce((sum, w) => sum + w.amount, 0)
            const winnerIds = new Set(winners.map(w => w.user_id))
            const nonWinners = contestants.filter(c => c.visible && !winnerIds.has(c.user_id))
            const nonWinnerTotal = nonWinners.length * NON_WINNER_AMOUNT
            const grandTotal = winnerTotal + nonWinnerTotal
            return (
              <>
                <h6 className='mt-3'>
                  ${NON_WINNER_AMOUNT} Participants ({nonWinners.length}):
                </h6>
                <ul className='list-unstyled'>
                  {nonWinners.map((c, idx) => (
                    <li key={idx} className='mb-1'>
                      <MDBIcon icon='user' className='text-info mr-2' />
                      <strong>{c.u_name || `${c.u_fname || ''} ${c.u_lname || ''}`.trim()}</strong>{' '}
                      <span className='text-info font-weight-bold'>${NON_WINNER_AMOUNT}</span>
                    </li>
                  ))}
                </ul>
                <p className='font-weight-bold'>
                  Total Awarded: ${grandTotal}
                  <br />
                  <small className='text-muted'>
                    (${winnerTotal} winners + ${nonWinnerTotal} from ${NON_WINNER_AMOUNT}/agent x {nonWinners.length}{' '}
                    non-winners)
                  </small>
                </p>
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
})

export default ContestController
